import { ConfirmModal } from 'app/components';
import { AttentionCircularRedIcon } from 'app/components/Icons/AttentionCircularIcon';
import {
  useApplicationsService,
  useQualificationsService,
} from 'modules/products/services/hooks';
import {
  TApplicationResponse,
  TQualificationRequestApplication,
} from 'modules/products/services/hooks/interfaces';
import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
} from 'react';
import { IQualificationChangeContext } from './qualificationChange.interfaces';
import { EApplicationStatus } from 'modules/products/services/hooks/useApplicationsService';

export const QualificationChangeContext = createContext<
  IQualificationChangeContext | undefined
>(undefined);

export interface IQualificationChangeProviderProps {
  children?: React.ReactNode;
}

export const QualificationChangeProvider = ({
  children,
}: IQualificationChangeProviderProps) => {
  const {
    cancelApplication,
    getQualificationRequestApplication,
    loadings,
    submitConsignee,
  } = useApplicationsService();
  const [
    cancelApplicationConfirmVisible,
    toggleCancelApplicationConfirmVisible,
  ] = useState<Partial<TApplicationResponse> | false>(false);
  const [subtmitConsigneeModal, setSubtmitConsigneeModal] =
    useState<Partial<TApplicationResponse>>();

  const {
    updateApplicationQualificationStatus,
    updateApplicationQualificationStatusLoading,
  } = useQualificationsService();
  const promiseRef = useRef<{
    resolve?: (
      payload:
        | {
            isQualification?: boolean;
            newStatus: string | TQualificationRequestApplication;
          }
        | false,
    ) => void;
  }>({
    resolve: undefined,
  });

  const onCancelApplication = useCallback(() => {
    if (cancelApplicationConfirmVisible && cancelApplicationConfirmVisible.id) {
      cancelApplication(
        cancelApplicationConfirmVisible.id,
        cancelApplicationConfirmVisible.originator?.id,
      ).then(() => {
        toggleCancelApplicationConfirmVisible(false);
        if (promiseRef.current?.resolve)
          promiseRef.current.resolve({
            isQualification: false,
            newStatus: EApplicationStatus.CANCELED,
          });
      });
    }
  }, [cancelApplicationConfirmVisible]);

  const submitConsigneeHandler = useCallback(() => {
    if (subtmitConsigneeModal?.id) {
      submitConsignee(subtmitConsigneeModal.id).then(() => {
        setSubtmitConsigneeModal(undefined);

        if (promiseRef.current?.resolve)
          promiseRef.current.resolve({
            isQualification: false,
            newStatus: EApplicationStatus.ISSUED,
          });
      });
    }
  }, [subtmitConsigneeModal]);

  const onCloseCancelConfirmationModal = useCallback(() => {
    toggleCancelApplicationConfirmVisible(false);
    if (promiseRef.current?.resolve) promiseRef.current.resolve(false);
  }, []);

  const handleUpdateQualificationStatus = useCallback(
    (
      application: Partial<TApplicationResponse>,
      identifier: string,
      qualification?: TQualificationRequestApplication,
    ) => {
      return new Promise<
        | {
            isQualification?: boolean;
            newStatus: string | TQualificationRequestApplication;
          }
        | false
      >((resolve) => {
        if (!application || !application.product || !application.id) {
          resolve(false);
          return;
        }
        if (
          ![
            EApplicationStatus.PENDING_QUALIFICATION,
            EApplicationStatus.PENDING_CESSION_QUALIFICATION,
          ].includes(application.status as EApplicationStatus) &&
          identifier === EApplicationStatus.CANCELED
        ) {
          toggleCancelApplicationConfirmVisible(application);
          promiseRef.current.resolve = resolve;
          return;
        }

        if (
          [
            EApplicationStatus.SENDING_CONSIGNEE_CONTRACT,
            EApplicationStatus.PENDING_CONTRACT_SUBMISSION,
          ].includes(application.status as EApplicationStatus) &&
          identifier === EApplicationStatus.ISSUED
        ) {
          setSubtmitConsigneeModal(application);
          promiseRef.current.resolve = resolve;
          return;
        }

        if (
          [
            EApplicationStatus.PENDING_QUALIFICATION,
            EApplicationStatus.PENDING_CESSION_QUALIFICATION,
          ].includes(application.status as EApplicationStatus)
        ) {
          if (identifier === EApplicationStatus.CANCELED) {
            toggleCancelApplicationConfirmVisible(application);
          } else if (qualification) {
            updateApplicationQualificationStatus(
              application.product.id,
              qualification.id,
              identifier,
              'BORROWER',
              application.originator?.id,
            ).then(() => {
              getQualificationRequestApplication(
                application?.product?.id || '',
                0,
                application?.borrower?.id || '',
                application.id,
                application.originator?.id,
                application.status as EApplicationStatus,
              ).then((qualificationRequests) => {
                resolve({
                  isQualification: true,
                  newStatus: qualificationRequests
                    ?.content[0] as TQualificationRequestApplication,
                });
              });
            });
          } else {
            resolve(false);
          }
        }
      });
    },
    [],
  );

  return (
    <QualificationChangeContext.Provider
      value={{
        handleUpdateQualificationStatus,
        handleUpdateQualificationStatusLoading:
          updateApplicationQualificationStatusLoading,
      }}
    >
      {children}
      <ConfirmModal
        isOpen={!!cancelApplicationConfirmVisible}
        icon={<AttentionCircularRedIcon />}
        title="Essa ação é irreversível, você tem certeza?"
        color="danger"
        btnConfirmText="Cancelar solicitação"
        isLoading={loadings.isCancelApplicationLoading}
        handleConfirm={onCancelApplication}
        handleClose={onCloseCancelConfirmationModal}
        handleCancel={onCloseCancelConfirmationModal}
      >
        Esta solicitação não poderá ser mais utilizada para pagamentos ou
        recebimentos. Créditos já solicitados ou concedidos não serão alterados.
      </ConfirmModal>

      <ConfirmModal
        isOpen={!!subtmitConsigneeModal}
        title="Essa ação é irreversível, você tem certeza?"
        color="attention"
        btnConfirmText="Emitir"
        isLoading={loadings.isSubmitConsigneeLoading}
        handleConfirm={submitConsigneeHandler}
        handleClose={() => setSubtmitConsigneeModal(undefined)}
        handleCancel={() => setSubtmitConsigneeModal(undefined)}
      >
        Esta solicitação não poderá ser alterada posteriormente.
      </ConfirmModal>
    </QualificationChangeContext.Provider>
  );
};

export const useQualificationChangeContext = () => {
  const context = useContext(QualificationChangeContext);

  if (context === undefined) {
    throw new Error(
      'useQualificationChangeContext must be used within a QualificationChangeProvider',
    );
  }
  return context;
};
